import { dayMs, hourMs, minuteMs } from "verdi-common/timeConstants";
import type { StateCreator } from "zustand";
import type { RelativeDuration, TimeRange } from "../ChartSpec";

export const DEFAULT_RELATIVE_DURATION: RelativeDuration = "7d";

/** Millisecond length of each relative preset — used to compute startMs from "now". */
export const PRESET_DURATION_MS: Record<RelativeDuration, number> = {
    "1y": 365 * dayMs,
    "90d": 90 * dayMs,
    "30d": 30 * dayMs,
    "7d": 7 * dayMs,
    "4d": 4 * dayMs,
    "1d": 1 * dayMs,
    "6h": 6 * hourMs,
    "1h": 1 * hourMs,
    "5m": 5 * minuteMs,
};

export type TimeRangeState = {
    /** Active relative preset, or null when the window is an absolute (fixed) range. */
    preset: RelativeDuration | null;
    startMs: number;
    endMs: number;
};

/** Slide a relative preset to end at now — the store's shape for live monitoring windows. */
function windowFromPreset(preset: RelativeDuration, nowMs = Date.now()): TimeRangeState {
    const durationMs = PRESET_DURATION_MS[preset];
    return { preset, startMs: nowMs - durationMs, endMs: nowMs };
}

/**
 * Convert a spec TimeRange (from DashboardSpec.defaultTimeRange) into runtime store state.
 * Relative ranges become a sliding window ending at now; absolute ranges keep fixed start/end with no preset.
 * Falls back to DEFAULT_RELATIVE_DURATION when the spec omits a default.
 */
function initialTimeRangeState(defaultTimeRange?: TimeRange, nowMs = Date.now()): TimeRangeState {
    if (!defaultTimeRange) {
        return windowFromPreset(DEFAULT_RELATIVE_DURATION, nowMs);
    }
    if (defaultTimeRange.mode === "relative") {
        return windowFromPreset(defaultTimeRange.duration, nowMs);
    }
    return {
        preset: null,
        startMs: new Date(defaultTimeRange.start).getTime(),
        endMs: new Date(defaultTimeRange.end).getTime(),
    };
}

export interface TimeRangeSlice extends TimeRangeState {
    setPreset: (preset: RelativeDuration) => void;
    /** Recompute the window from the current preset, anchored to now. No-op for absolute ranges. */
    advanceWindowToNow: () => void;
}

/**
 * Zustand slice for the shared time window.
 */
export const createTimeRangeSlice =
    (defaultTimeRange?: TimeRange): StateCreator<TimeRangeSlice, [], [], TimeRangeSlice> =>
    (set, get) => ({
        ...initialTimeRangeState(defaultTimeRange),
        setPreset: (preset) => set(windowFromPreset(preset)),
        advanceWindowToNow: () => {
            const { preset } = get();
            if (preset) {
                set(windowFromPreset(preset));
            }
        },
    });
